/**
 * Minimal Protoo-over-WebSocket client: request/response, server requests, notifications,
 * plus its own reconnect policy. One class, deliberately not split into smaller files —
 * every piece of it (the raw `WebSocket`, the pending-request map, the reconnect-attempt
 * counter) is state a single connection owns and reads/writes synchronously; splitting
 * it up would just mean passing that same state between files instead of methods.
 *
 * Sections below, in order: connection lifecycle (constructor → `close()`) → reconnect
 * policy (what happens when the socket closes) → opening the actual WebSocket → inbound
 * message routing → outbound requests/responses/notifications.
 */

import { RECONNECT_MAX_RETRIES, reconnectDelayMs } from "./reconnect";
import type {
  ProtooNotificationMessage,
  ProtooNotifyMap,
  ProtooRequestMap,
  ProtooRequestMessage,
} from "./protoo-messages";

export interface ProtooClientOptions {
  debug?: (msg: string) => void;
  logWire?: boolean;
}

interface ProtooResponseMessage {
  response: true;
  id: number;
  ok: boolean;
  data?: unknown;
  errorReason?: string;
}

type ProtooInboundMessage =
  ProtooRequestMessage | ProtooNotificationMessage | ProtooResponseMessage;

interface Waiter {
  resolve: (v: ProtooResponseMessage) => void;
  reject: (e: Error) => void;
  tid: ReturnType<typeof setTimeout>;
}

export class ProtooClient {
  url: string;
  ws: WebSocket | null = null;
  seq = 0;
  waiters = new Map<number, Waiter>();
  onRequest: ((msg: ProtooRequestMessage) => void) | null = null;
  onNotification: ((msg: ProtooNotificationMessage) => void) | null = null;
  onOpen: ((ev: { isReconnect: boolean }) => void | Promise<void>) | null = null;
  onDisconnected: (() => void) | null = null;
  onFatalClose: ((ev: { code: number; reason: string; wasClean: boolean }) => void) | null = null;
  onError: (() => void) | null = null;

  private _debug: (msg: string) => void;
  /** Логировать каждое входящее WS-сообщение (кроме ответов на request). */
  private _logWire: boolean;
  private _closed = false;
  private _connectedOnce = false;
  private _reconnectAttempt = 0;
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _abortSignal: AbortSignal | null = null;
  private _abortListener: (() => void) | null = null;
  private _intentionalReconnect = false;

  /** @param url WebSocket URL (incl. roomId / peerId query if needed) */
  constructor(url: string, options: ProtooClientOptions = {}) {
    this.url = url;
    this._debug = options.debug ?? (() => {});
    this._logWire = options.logWire ?? false;
  }

  // --- Connection lifecycle -------------------------------------------------------

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  failWaiters(reason: string | Error): void {
    const err =
      reason instanceof Error ? reason : new Error(String(reason || "ProtooClient closed"));
    for (const [, w] of this.waiters) {
      clearTimeout(w.tid);
      w.reject(err);
    }
    this.waiters.clear();
  }

  private _clearReconnectTimer(): void {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
  }

  // --- Reconnect policy: what happens when the socket closes ----------------------

  private _handleWsClose(ev: { code: number; reason: string; wasClean: boolean }): void {
    this._debug(`protoo WS close code=${ev.code} reason=${ev.reason || "-"} clean=${ev.wasClean}`);
    this.ws = null;
    this.failWaiters(`Protoo WebSocket closed code=${ev.code} reason=${ev.reason || "-"}`);

    if (this._closed) {
      // Deliberate close() — the caller already knows, nothing to report.
      return;
    }

    if (!this._connectedOnce) {
      this.onFatalClose?.({ code: ev.code, reason: ev.reason || "", wasClean: ev.wasClean });
      return;
    }

    if (this._intentionalReconnect) {
      this._intentionalReconnect = false;
      void this._runWebSocket(true);
      return;
    }

    this.onDisconnected?.();
    this._scheduleReconnect();
  }

  private _scheduleReconnect(): void {
    if (this._closed || this._reconnectTimer) return;

    if (this._reconnectAttempt >= RECONNECT_MAX_RETRIES) {
      this._debug(`protoo reconnect exhausted after ${RECONNECT_MAX_RETRIES} attempts`);
      this.onFatalClose?.({ code: 1006, reason: "reconnect exhausted", wasClean: false });
      return;
    }

    const attempt = this._reconnectAttempt;
    const delay = reconnectDelayMs(attempt);
    this._reconnectAttempt += 1;
    this._debug(
      `[reconnect] signaling retry ${this._reconnectAttempt}/${RECONNECT_MAX_RETRIES} in ${delay}ms`
    );

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (this._closed) return;
      void this._runWebSocket(true);
    }, delay);
  }

  // --- Opening the actual WebSocket ------------------------------------------------

  private _wireWebSocket(ws: WebSocket, isReconnect: boolean): void {
    // Protoo always sends text frames — annotating the listener itself as
    // MessageEvent<string> (rather than the DOM lib's MessageEvent<any> default) is what
    // lets `ev.data` come out as `string` below, no cast needed.
    ws.addEventListener("message", (ev: MessageEvent<string>) => {
      try {
        const msg = JSON.parse(ev.data) as ProtooInboundMessage;
        const isResponse = "response" in msg && msg.response === true;
        if (this._logWire && !isResponse) {
          const m = "method" in msg ? msg.method : "-";
          const isMediasoupVersionNotif =
            "notification" in msg && msg.notification === true && m === "mediasoupVersion";
          if (!isMediasoupVersionNotif) {
            this._debug(
              `protoo rx method=${m} req=${"request" in msg && Boolean(msg.request)} notif=${"notification" in msg && Boolean(msg.notification)}`
            );
          }
        }
        this._handleMessage(msg);
      } catch (e) {
        this._debug(`protoo rx parse/handle error: ${e}`);
      }
    });

    ws.addEventListener("close", (ev: CloseEvent) => {
      this._handleWsClose({
        code: ev.code,
        reason: ev.reason || "",
        wasClean: ev.wasClean,
      });
    });

    ws.addEventListener("error", () => {
      this._debug(`protoo WS error readyState=${this.ws?.readyState}`);
      this.onError?.();
    });

    ws.addEventListener("open", () => {
      this._connectedOnce = true;
      this._reconnectAttempt = 0;
      this._debug(`protoo WS open reconnect=${isReconnect}`);
      void this.onOpen?.({ isReconnect });
    });
  }

  private async _runWebSocket(isReconnect: boolean): Promise<void> {
    if (this._closed) return;
    if (this._abortSignal?.aborted) return;

    const ws = new WebSocket(this.url, "protoo");
    this.ws = ws;
    this._wireWebSocket(ws, isReconnect);

    // Stage timeout: if the socket never opens (or closes) within the budget — a
    // silent network stall, no TCP RST, nothing — force-close it so the existing
    // close-handling path (fatal-close for the initial connect, backoff retry for a
    // reconnect attempt) runs instead of hanging forever with no error surfaced.
    const timeout = 5000;
    const connectTimer = setTimeout(() => {
      if (ws.readyState === WebSocket.CONNECTING) {
        this._debug(`protoo WS connect timeout after ${timeout}ms`);
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      }
    }, timeout);
    ws.addEventListener("open", () => clearTimeout(connectTimer), { once: true });
    ws.addEventListener("close", () => clearTimeout(connectTimer), { once: true });

    if (!isReconnect) {
      const hint =
        "Check mediasoup-demo (e.g. :4443). If Origin is restricted, use http://localhost:8080 not 127.0.0.1.";
      await new Promise<void>((resolve, reject) => {
        let done = false;
        const finish = (fn: () => void) => {
          if (done) return;
          done = true;
          ws.removeEventListener("open", onOpen);
          ws.removeEventListener("error", onError);
          ws.removeEventListener("close", onClose);
          fn();
        };
        const onOpen = () => finish(resolve);
        const onError = () =>
          finish(() => reject(new Error(`WebSocket error connecting to ${this.url}. ${hint}`)));
        const onClose = (ev: CloseEvent) => {
          if (ws.readyState === WebSocket.OPEN) return;
          finish(() =>
            reject(new Error(`WebSocket closed before open (${this.url}) code=${ev.code} ${hint}`))
          );
        };
        ws.addEventListener("open", onOpen);
        ws.addEventListener("error", onError);
        ws.addEventListener("close", onClose);
      });
    }
  }

  async connect(opts: { signal?: AbortSignal } = {}): Promise<void> {
    const { signal } = opts;
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    this._closed = false;
    this._connectedOnce = false;
    this._reconnectAttempt = 0;
    this._abortSignal = signal ?? null;

    const onAbort = () => {
      this.close();
    };
    this._abortListener = onAbort;
    signal?.addEventListener("abort", onAbort, { once: true });

    try {
      await this._runWebSocket(false);
    } catch (err) {
      this._clearReconnectTimer();
      throw err;
    }
  }

  /** Force immediate WS reconnect (e.g. DevMenu). */
  reconnectNow(): void {
    if (this._closed) return;
    this._clearReconnectTimer();
    this._reconnectAttempt = 0;
    this._intentionalReconnect = true;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
    } else {
      this._intentionalReconnect = false;
      void this._runWebSocket(true);
    }
  }

  // --- Inbound message routing ------------------------------------------------------

  private _handleMessage(msg: ProtooInboundMessage): void {
    if ("response" in msg && msg.response === true && msg.id != null) {
      const w = this.waiters.get(msg.id);
      if (!w) return;
      this.waiters.delete(msg.id);
      clearTimeout(w.tid);
      w.resolve(msg);
      return;
    }
    if ("request" in msg && msg.request === true && this.onRequest) {
      this.onRequest(msg);
      return;
    }
    if ("notification" in msg && msg.notification === true && this.onNotification) {
      this.onNotification(msg);
    }
  }

  // --- Outbound: requests, responses, notifications ---------------------------------

  async request<M extends keyof ProtooRequestMap>(
    method: M,
    data: ProtooRequestMap[M]["params"]
  ): Promise<ProtooRequestMap[M]["result"]> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error("ProtooClient not connected");
    }
    this.seq += 1;
    const id = this.seq;
    const p = new Promise<ProtooResponseMessage>((resolve, reject) => {
      const tid = setTimeout(() => {
        if (this.waiters.delete(id)) reject(new Error(`Request timeout: ${String(method)}`));
      }, 5000);
      this.waiters.set(id, { resolve, reject, tid });
    });
    this.ws.send(JSON.stringify({ request: true, id, method, data }));
    const res = await p;
    if (!res.ok) throw new Error(`${String(method)}: ${res.errorReason || "error"}`);
    // The server's response payload is inherently untyped JSON — this cast is the one
    // place that asserts it matches the method's documented result shape.
    return (res.data ?? {}) as ProtooRequestMap[M]["result"];
  }

  sendResponse(
    id: number,
    ok = true,
    data: Record<string, unknown> = {},
    errorReason: string | null = null,
    errorCode = 500
  ): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const payload: Record<string, unknown> = { response: true, id, ok, data };
    if (!ok) {
      payload.errorReason = errorReason || "request failed";
      payload.errorCode = errorCode;
    }
    this.ws.send(JSON.stringify(payload));
  }

  notify<M extends keyof ProtooNotifyMap>(method: M, data: ProtooNotifyMap[M]): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ notification: true, method, data }));
  }

  /** Close WebSocket, stop retries, and reject pending requests. */
  close(): void {
    this._closed = true;
    this._clearReconnectTimer();
    this.failWaiters("ProtooClient closed");
    if (this._abortListener && this._abortSignal) {
      this._abortSignal.removeEventListener("abort", this._abortListener);
    }
    this._abortListener = null;
    this._abortSignal = null;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
  }
}
